import { FormattedMessage, useIntl } from 'react-intl';
import { TravellerWithCollectionMembershipsDTO } from '@/api/trafikportalen';
import IconButton from '../core/IconButton';
import { AllpayUsers } from './AllpayUsers';
import { IconChevronDown, IconSmartcard, IconSmartphoneDevice } from '@/icons';
import { TravellerBearers } from './TravellerBearers';
import { CreditCards } from './CreditCards';
import { ContactInformationForm } from './ContactInformationForm';
import { useTravellerCreditInfo } from '@/hooks/useTravellerCreditInfo';
import { Section } from '../Section';
import { getTravellerStatus } from '@/helpers/status';
import { TravellerMemberships } from './TravellerMemberships';
import { isStudentCollectionType } from '@/helpers/collectionTypes';

interface ProfileFormProps {
  traveller: TravellerWithCollectionMembershipsDTO;
}

export const ProfileForm = ({ traveller }: ProfileFormProps) => {
  const status = getTravellerStatus(traveller);
  const hasNonStudentMembership = traveller.memberships.some(
    (m) => !isStudentCollectionType(m.collection_type),
  );

  return (
    <>
      {hasNonStudentMembership && status !== 'inactive' ? (
        <TravellerCredit traveller={traveller} />
      ) : null}
      <ContactInformationForm traveller={traveller} />
      <TravellerBearers traveller={traveller} />
      {hasNonStudentMembership && (
        <>
          <AllpayUsers traveller={traveller} />
          <CreditCards traveller={traveller} />
          <TravellerMemberships traveller={traveller} />
        </>
      )}
    </>
  );
};

const TravellerCredit = ({
  traveller,
}: {
  traveller: TravellerWithCollectionMembershipsDTO;
}) => {
  const { formatMessage, locale } = useIntl();
  const { allpayCredit, cardCredit } = useTravellerCreditInfo(traveller);

  return (
    <div className="flex w-full flex-wrap gap-4">
      <Section className="min-w-[300px] grow basis-[calc(50%-0.5rem)]">
        <p className="flex items-center gap-3 text-2xl font-bold">
          <IconSmartphoneDevice className="size-8 min-w-8 text-allpay" />
          {allpayCredit.toLocaleString(locale)}
          <span className="text-base font-normal text-text-700">
            <FormattedMessage defaultMessage="SEK" />
          </span>
          <IconButton
            aria-label={formatMessage({
              defaultMessage: 'Scrolla till tjänsteresa i app',
            })}
            icon={<IconChevronDown />}
            variant="ghost"
            className="ml-auto"
            title={formatMessage({
              defaultMessage: 'Scrolla till tjänsteresa i app',
            })}
            onClick={() => {
              const section = document.getElementById('allpay-users');
              if (section) {
                section.scrollIntoView({
                  behavior: 'smooth',
                  block: 'center',
                });
              }
            }}
          />
        </p>
        <p className="leading-4">
          <FormattedMessage defaultMessage="Tjänsteresa i app per månad" />
        </p>
      </Section>
      <Section className="min-w-[300px] grow basis-[calc(50%-0.5rem)]">
        <p className="flex items-center gap-3 text-2xl font-bold">
          <IconSmartcard className="size-8 min-w-8 text-smartcard" />
          {cardCredit.toLocaleString(locale)}
          <span className="text-base font-normal text-text-700">
            <FormattedMessage defaultMessage="SEK" />
          </span>
          <IconButton
            aria-label={formatMessage({
              defaultMessage: 'Scrolla till tjänsteresa på kort',
            })}
            icon={<IconChevronDown />}
            variant="ghost"
            className="ml-auto"
            title={formatMessage({
              defaultMessage: 'Scrolla till tjänsteresa på kort',
            })}
            onClick={() => {
              const section = document.getElementById('credit-cards');
              if (section) {
                section.scrollIntoView({
                  behavior: 'smooth',
                  block: 'center',
                });
              }
            }}
          />
        </p>
        <p className="leading-4">
          <FormattedMessage defaultMessage="Tjänsteresa på kort per månad" />
        </p>
      </Section>
    </div>
  );
};
